import React, { useEffect, useMemo, useRef } from 'react';
import template from './newHereTemplate';
import { primaryLockupHtml, PORTAL_SPIN_CSS } from '../../utils/portalWordmark';
import animateLockups from '../../utils/portalLogoMotion';

// The export set the logo as type (a 22px ball + "Portal" in the header, a big
// "Portal" line in the footer). The wordmark is drawn geometry now, so both are
// swapped for the primary lockup (the portal IS the O) before the document is handed
// to the iframe: cap 24 in the header, large in the footer. "Create your Portal" in
// the body is a sentence, not the logo, and stays type. The template is generated,
// so it is patched here rather than edited; NewHereLanding.test.js fails if a
// re-export ever stops matching.
const HEADER_LOGO = /<img src="\/landing\/pr-disco-ball-logo\.svg"[^>]*>Portal<\/a>/;
// The hero's standalone spinning ball (a GIF) becomes the lockup too, as on the main
// landing. Its parent is a centred flex column and the lockup has no intrinsic
// width, so the width is in vw — a percentage there collapses to 0. The ground is a
// fixed light grey, so the word is black.
const HERO_BALL = /<img src="\/newhere\/newhere\.gif"[^>]*>/;
const FOOTER_WORD = /(<div style="[^"]*clamp\(34px, 7\.6vw, 118px\)[^"]*)">Portal<\/div>/;

export const withLockups = (html) => html
  // The iframe doesn't load index.css, so the portal's spin rides in with the markup.
  .replace(/<\/head>/i, `<style>${PORTAL_SPIN_CSS}</style></head>`)
  .replace(HERO_BALL, `<div style="color:#000000;margin-bottom:34px;">${primaryLockupHtml({ width: 'min(450px, 80vw)' })}</div>`)
  // The export's sign-in / sign-up links were absolute partyresume.com URLs, which
  // walked a heyportal.app visitor over to the old domain. Same app, same origin.
  .replace(/href="https:\/\/partyresume\.com\//g, 'href="/')
  .replace(HEADER_LOGO, `${primaryLockupHtml({ width: '150px' })}</a>`)
  .replace(FOOTER_WORD, `$1;line-height:0;padding-bottom:clamp(0px, 1.2vw, 16px);">${primaryLockupHtml({ width: 'clamp(175px, 31.25vw, 450px)' })}</div>`);

/**
 * NewHereLanding — the "New in town? You're invited" landing, exactly as designed
 * (docs export). Rendered in a same-origin iframe so its full design-system CSS +
 * fonts stay perfectly isolated from the app. This is what a LOGGED-OUT visitor
 * sees in place of a city's City Portal, with per-city content dropped into the
 * header and the sample signal.
 *
 * Props are the dynamic-per-city bits: the city name in the headline and a signal
 * calling out a real local venue.
 */
export default function NewHereLanding({
  city = 'Penticton',
  signalName = 'Sam K.',
  signalMeta = 'Live til 9 pm',
  signalText = 'Trivia at the Cannery tonight. Who’s with me?',
}) {
  const html = useMemo(() => withLockups(template)
    // Links live inside an iframe; <base target="_top"> makes them navigate the whole
    // window (to /login, /register) instead of loading the app inside the iframe.
    .replace(/<head>/i, '<head><base target="_top">')
    .replace(/\{\{CITY\}\}/g, (city || 'your town').toUpperCase())
    .replace(/\{\{SIGNAL_NAME\}\}/g, signalName || 'Someone')
    .replace(/\{\{SIGNAL_META\}\}/g, signalMeta || 'Live tonight')
    .replace(/\{\{SIGNAL_TEXT\}\}/g, signalText || 'Something good tonight. Who’s with me?'),
  [city, signalName, signalMeta, signalText]);

  // The iframe is same-origin (srcDoc), so the parent can reach in and run the
  // trace through the wordmarks. Re-run on every load: a new srcDoc is a new document.
  const frameRef = useRef(null);
  const stopRef = useRef(() => {});
  const spin = () => {
    stopRef.current();
    stopRef.current = animateLockups(frameRef.current?.contentDocument);
  };
  useEffect(() => () => stopRef.current(), []);

  return (
    <iframe
      ref={frameRef}
      onLoad={spin}
      title="New in town? You’re invited · Portal"
      srcDoc={html}
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', border: 0 }}
    />
  );
}
